import PropTypes from 'prop-types';
import React, {createContext, useContext, useRef} from 'react';

import {RECAPTCHA_SITE_KEY} from '../../constants/recaptcha';
import {useRecaptchaConfig} from '../../hooks/useRecaptchaConfig';
import {recaptchaLogger} from '../../utils/recaptchaMonitoring';

import ReCaptcha from './ReCaptcha';

const RecaptchaContext = createContext();

export const useRecaptchaContext = () => {
  const context = useContext(RecaptchaContext);
  if (!context) {
    throw new Error(
      'useRecaptchaContext deve ser usado dentro de RecaptchaProvider'
    );
  }
  return context;
};

export const RecaptchaProvider = ({children}) => {
  const recaptchaRef = useRef();
  const {config} = useRecaptchaConfig();

  const executeRecaptcha = React.useCallback(
    async (action) => {
      if (!config.ENABLED) {
        recaptchaLogger.info('reCAPTCHA desabilitado', {action});
        return null;
      }

      if (!recaptchaRef.current) {
        recaptchaLogger.warn('reCAPTCHA ref não disponível', {action});
        return null;
      }

      try {
        const token = await recaptchaRef.current.executeAsync(action);
        recaptchaLogger.info('Token reCAPTCHA gerado', {
          action,
          hasToken: !!token
        });
        return token;
      } catch (error) {
        recaptchaLogger.error('Erro ao executar reCAPTCHA', {
          action,
          error: error.message
        });
        return null;
      }
    },
    [config]
  );

  const resetRecaptcha = React.useCallback(() => {
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, []);

  const contextValue = React.useMemo(
    () => ({
      executeRecaptcha,
      resetRecaptcha,
      isEnabled: config.ENABLED
    }),
    [executeRecaptcha, resetRecaptcha, config.ENABLED]
  );

  return (
    <RecaptchaContext.Provider value={contextValue}>
      {children}
      {config.ENABLED && (
        <ReCaptcha ref={recaptchaRef} siteKey={RECAPTCHA_SITE_KEY} />
      )}
    </RecaptchaContext.Provider>
  );
};

RecaptchaProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default RecaptchaProvider;
